import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import JobDetailModal from '../../components/consultant/JobDetailModal';
import {
  ArrowLeft, Briefcase, DollarSign, Calendar, Search, Filter,
  Image as ImageIcon, Building2, Users, Star, EyeOff,
  MessageSquare, Mail, Send, Plus, ChevronDown, ChevronUp, CheckCircle,
  Clock, MoreVertical, Heart, AlertCircle, RefreshCw, ExternalLink,
  Layers, ChevronRight, User, Inbox, X
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface JobPosting {
  id: string;
  job_title: string;
  requisition_number: string;
  job_description: string;
  skills_qualifications: string;
  worker_type: string;
  compensation_amount: number;
  comp_grade: string;
  hire_deadline_date: string;
  supervisory_organization: string;
  job_family: string;
  status: string;
  is_pro_posting?: boolean;
  created_at: string;
  customer_id: string;
  company_name?: string;
  background_image_url?: string | null;
  company_profile_image_url?: string | null;
  career_page_url?: string | null;
}

interface JobApplication {
  id: string;
  job_posting_id: string;
  status: string;
  applied_at: string;
  cover_letter?: string;
  resume_url?: string;
  question_answers?: Array<{ question: string; answer: string }>;
  job_title?: string;
  company_name?: string;
  is_pro_posting?: boolean;
  worker_type?: string;
  hire_deadline_date?: string;
  is_complete?: boolean;
}

interface CandidateMessage {
  id: string;
  client_id: string;
  consultant_id: string;
  sender_type: 'client' | 'consultant';
  message_body: string;
  message_type?: string;
  metadata?: {
    application_id?: string;
    job_posting_id?: string;
    job_title?: string;
    company_name?: string;
    proposed_slots?: Array<{ date: string; time: string }>;
    confirmed_slot?: { date: string; time: string };
    slots?: Array<{ date: string; time: string }>;
    accepted_slot?: { date: string; time: string };
    note?: string;
  } | null;
  is_read: boolean;
  created_at: string;
}

interface ClientConv {
  clientId: string;
  clientName: string;
  clientPhotoUrl?: string;
  lastMsg: CandidateMessage | null;
  unread: number;
  canReply: boolean;
}

type PageTab = 'opportunities' | 'applications';
type AppFilter = 'all' | 'pending' | 'pre_selected' | 'interview_scheduled' | 'hired' | 'rejected' | 'incomplete';
type MsgMode = 'list' | 'thread';

// ─── Status config ────────────────────────────────────────────────────────────

const APP_STATUS: Record<string, { label: string; badge: string }> = {
  pending:             { label: 'Pending',                      badge: 'bg-yellow-100 text-yellow-800 border border-yellow-200' },
  reviewed:            { label: 'Pre-Screened',                 badge: 'bg-blue-100 text-blue-800 border border-blue-200' },
  shortlisted:         { label: 'Pre-Selected',                 badge: 'bg-green-100 text-green-800 border border-green-200' },
  interview_scheduled: { label: 'Interview',                    badge: 'bg-teal-100 text-teal-800 border border-teal-200' },
  rejected:            { label: 'Filled with another Candidate',badge: 'bg-red-100 text-red-800 border border-red-200' },
  hired:               { label: 'Offer',                        badge: 'bg-emerald-100 text-emerald-800 border border-emerald-200' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(dateStr: string) {
  const ms = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ─── DotMenu ─────────────────────────────────────────────────────────────────

function DotMenu({ items }: { items: { label: string; icon?: React.ReactNode; onClick: () => void; danger?: boolean }[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  return (
    <div ref={ref} className="relative">
      <button onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
        className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors">
        <MoreVertical className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-52 rounded-xl border border-gray-200 bg-white shadow-xl z-50 overflow-hidden">
          {items.map((item, i) => (
            <button key={i} onClick={() => { item.onClick(); setOpen(false); }}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm transition-colors ${item.danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700 hover:bg-gray-50'}`}>
              {item.icon}<span>{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Avatar ───────────────────────────────────────────────────────────────────

function Avatar({ name, photoUrl, size = 'md' }: { name: string; photoUrl?: string; size?: 'sm' | 'md' }) {
  const [err, setErr] = useState(false);
  const initials = name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2) || '?';
  const sz = size === 'sm' ? 'w-8 h-8 text-xs' : 'w-10 h-10 text-sm';
  if (photoUrl && !err) {
    return <img src={photoUrl} alt="" onError={() => setErr(true)} className={`${sz} rounded-full object-cover border-2 border-blue-400 flex-shrink-0`} />;
  }
  return (
    <div className={`${sz} rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold flex-shrink-0`}>
      {initials}
    </div>
  );
}

// ─── Candidate Messaging — mirrors AdminMessages exactly ──────────────────────

function CandidateMessagingPanel({ consultantId }: { consultantId: string }) {
  const [convs, setConvs] = useState<ClientConv[]>([]);
  const [mode, setMode] = useState<MsgMode>('list');
  const [activeConv, setActiveConv] = useState<ClientConv | null>(null);
  const [msgs, setMsgs] = useState<CandidateMessage[]>([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const endRef = useRef<HTMLDivElement>(null);
  // Pre-screen scheduling state
  const [scheduleModal, setScheduleModal] = useState<CandidateMessage | null>(null);
  const [scheduleMode, setScheduleMode] = useState<'accept' | 'propose'>('accept');
  const [acceptedSlot, setAcceptedSlot] = useState<{ date: string; time: string } | null>(null);
  const [availSlots, setAvailSlots] = useState([{ date: '', time: '' }, { date: '', time: '' }, { date: '', time: '' }]);
  const [submittingAvail, setSubmittingAvail] = useState(false);

  useEffect(() => { loadConvs(); }, [consultantId]);

  useEffect(() => {
    const ch = supabase.channel('opp-cand-msgs-' + consultantId)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'candidate_messages', filter: `consultant_id=eq.${consultantId}` }, () => {
        loadConvs();
        if (activeConv) loadThread(activeConv.clientId);
      }).subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [consultantId, activeConv]);

  useEffect(() => {
    if (endRef.current && mode === 'thread') endRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [msgs, mode]);

  const loadConvs = async () => {
    const { data: allMsgs } = await supabase
      .from('candidate_messages').select('*').eq('consultant_id', consultantId).order('created_at', { ascending: false });
    if (!allMsgs) { setLoading(false); return; }

    const clientIds = [...new Set(allMsgs.map(m => m.client_id))];
    const { data: profiles } = clientIds.length > 0
      ? await supabase.from('workday_client_profiles').select('id, first_name, last_name, photo_url').in('id', clientIds)
      : { data: [] };

    const map = new Map<string, ClientConv>();
    for (const msg of allMsgs) {
      if (!map.has(msg.client_id)) {
        const p = profiles?.find((x: any) => x.id === msg.client_id);
        map.set(msg.client_id, {
          clientId: msg.client_id,
          clientName: p ? `${p.first_name} ${p.last_name}` : 'Hiring Client',
          clientPhotoUrl: p?.photo_url || undefined,
          lastMsg: msg,
          unread: 0,
          canReply: false,
        });
      }
      if (msg.sender_type === 'client' && !msg.is_read) {
        map.get(msg.client_id)!.unread++;
      }
    }
    for (const [cid, conv] of map.entries()) {
      conv.canReply = allMsgs.some(m => m.client_id === cid && m.sender_type === 'client');
    }
    setConvs([...map.values()]);
    setLoading(false);
  };

  const loadThread = async (clientId: string) => {
    setLoadingMsgs(true);
    const { data } = await supabase.from('candidate_messages').select('*')
      .eq('client_id', clientId).eq('consultant_id', consultantId).order('created_at', { ascending: true });
    setMsgs(data || []);
    for (const m of (data || []).filter(m => m.sender_type === 'client' && !m.is_read)) {
      await supabase.from('candidate_messages').update({ is_read: true }).eq('id', m.id);
    }
    loadConvs();
    setLoadingMsgs(false);
  };

  const openThread = (conv: ClientConv) => {
    setActiveConv(conv);
    loadThread(conv.clientId);
    setMode('thread');
  };

  const submitAvailability = async () => {
    if (!scheduleModal || !activeConv) return;
    setSubmittingAvail(true);
    const appId = scheduleModal.metadata?.application_id;
    const jobTitle = scheduleModal.metadata?.job_title || 'the position';
    if (scheduleMode === 'accept' && acceptedSlot) {
      const label = `${new Date(acceptedSlot.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} at ${acceptedSlot.time}`;
      await supabase.from('candidate_messages').insert({
        client_id: activeConv.clientId, consultant_id: consultantId, sender_type: 'consultant',
        message_body: `I can make the pre-screen interview for ${jobTitle} on ${label}.`,
        message_type: 'prescreen_availability',
        metadata: { application_id: appId, slots: [acceptedSlot], accepted_slot: acceptedSlot },
      });
    } else {
      const validSlots = availSlots.filter(s => s.date && s.time);
      if (validSlots.length === 0) { setSubmittingAvail(false); return; }
      const slotLabels = validSlots.map(s => `• ${new Date(s.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} at ${s.time}`).join('\n');
      await supabase.from('candidate_messages').insert({
        client_id: activeConv.clientId, consultant_id: consultantId, sender_type: 'consultant',
        message_body: `My availability for the Pre-Screen Interview (${jobTitle}):\n${slotLabels}`,
        message_type: 'prescreen_availability',
        metadata: { application_id: appId, slots: validSlots },
      });
    }
    if (appId) await supabase.from('job_applications').update({ prescreen_status: 'availability_submitted' }).eq('id', appId);
    setScheduleModal(null); setScheduleMode('accept'); setAcceptedSlot(null);
    setAvailSlots([{ date: '', time: '' }, { date: '', time: '' }, { date: '', time: '' }]);
    loadThread(activeConv.clientId);
    setSubmittingAvail(false);
  };

  const handleReply = async () => {
    if (!reply.trim() || !activeConv) return;
    setSending(true);
    await supabase.from('candidate_messages').insert({
      client_id: activeConv.clientId,
      consultant_id: consultantId,
      sender_type: 'consultant',
      message_body: reply.trim(),
    });
    setReply('');
    loadThread(activeConv.clientId);
    setSending(false);
  };

  const goBack = () => { setMode('list'); setActiveConv(null); setMsgs([]); setReply(''); };

  const totalUnread = convs.reduce((s, c) => s + c.unread, 0);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-gray-200 rounded w-1/3" />
          <div className="h-12 bg-gray-200 rounded" />
          <div className="h-12 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">

      {/* Header — exact AdminMessages pattern */}
      <div className="p-4 border-b border-gray-200 bg-blue-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {mode === 'thread' && (
              <button onClick={goBack} className="p-1 text-blue-600 hover:text-blue-800 transition-colors">
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <Mail className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-gray-900">
              {mode === 'thread' && activeConv ? activeConv.clientName : 'Messages from Hiring Companies'}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            {totalUnread > 0 && mode === 'list' && (
              <span className="px-3 py-1 bg-blue-600 text-white text-sm font-medium rounded-full">
                {totalUnread} new
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Thread detail — bubble layout mirrors AdminMessages exactly */}
      {mode === 'thread' && activeConv && (
        <div className="flex flex-col">
          {/* Sub-header */}
          <div className="p-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {activeConv.canReply
                ? <span className="text-xs text-green-600 flex items-center gap-1"><CheckCircle className="w-3 h-3" />Active</span>
                : <span className="text-xs text-gray-500 flex items-center gap-1"><Clock className="w-3 h-3" />Awaiting client message</span>}
            </div>
            <DotMenu items={[
              { label: 'Back to inbox', icon: <Inbox className="w-3.5 h-3.5" />, onClick: goBack },
            ]} />
          </div>

          {/* Bubbles */}
          <div className="p-4 max-h-64 overflow-y-auto space-y-3">
            {loadingMsgs ? (
              <div className="text-center py-6 text-gray-400 text-sm">Loading messages…</div>
            ) : msgs.length === 0 ? (
              <div className="text-center py-6 text-gray-400 text-sm">No messages yet</div>
            ) : msgs.map(msg => {
              const isPrescreenReq = msg.message_type === 'prescreen_request' || msg.message_type === 'prescreen_new_times';
              const isPrescreenAvail = msg.message_type === 'prescreen_availability';
              const isPrescreenConfirmed = msg.message_type === 'prescreen_confirmed';
              if (isPrescreenReq && msg.sender_type === 'client') {
                const slots = msg.metadata?.proposed_slots || [];
                const isNewTimes = msg.message_type === 'prescreen_new_times';
                const hasSlots = slots.length > 0;
                return (
                  <div key={msg.id} className="flex justify-start">
                    <div className="w-full max-w-xs rounded-2xl border-2 border-teal-300/50 overflow-hidden bg-white shadow-sm">
                      <div className="bg-gradient-to-r from-blue-50 to-teal-50 px-4 py-3 border-b border-teal-200/40">
                        <div className="flex items-center gap-2 mb-0.5">
                          <Calendar className="w-4 h-4 text-teal-500" />
                          <span className="text-sm font-bold text-gray-800">{isNewTimes ? 'New Times Proposed' : 'Schedule Pre-Screen Interview'}</span>
                        </div>
                        <p className="text-xs text-gray-500">{msg.metadata?.company_name} · {msg.metadata?.job_title}</p>
                      </div>
                      <div className="px-4 py-3 space-y-3">
                        {hasSlots && (
                          <div>
                            <p className="text-xs font-semibold text-gray-500 mb-2">Available times from hiring company:</p>
                            <div className="space-y-1.5">
                              {slots.map((s, i) => (
                                <div key={i} className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg bg-gray-50 border border-gray-200">
                                  <Calendar className="w-3 h-3 text-teal-400 flex-shrink-0" />
                                  <span className="font-medium text-gray-700">{new Date(s.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                                  <span className="text-gray-400">at {s.time}</span>
                                </div>
                              ))}
                              {msg.metadata?.note && <p className="text-xs mt-1 italic text-gray-400">"{msg.metadata.note}"</p>}
                            </div>
                          </div>
                        )}
                        {!hasSlots && <p className="text-xs text-gray-500">Please share up to 3 times when you're available for a brief pre-screen call.</p>}
                        <div className="flex gap-2">
                          {hasSlots && (
                            <button onClick={() => { setScheduleModal(msg); setScheduleMode('accept'); setAcceptedSlot(null); }}
                              className="flex-1 py-2 rounded-xl bg-teal-400 text-black text-xs font-semibold hover:bg-teal-400/90 transition-colors flex items-center justify-center gap-1.5">
                              <CheckCircle className="w-3.5 h-3.5" />Accept a Time
                            </button>
                          )}
                          <button onClick={() => { setScheduleModal(msg); setScheduleMode('propose'); setAvailSlots([{ date: '', time: '' }, { date: '', time: '' }, { date: '', time: '' }]); }}
                            className="flex-1 py-2 rounded-xl text-xs font-semibold border border-teal-400 text-teal-600 hover:bg-teal-50 transition-colors flex items-center justify-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5" />{hasSlots ? 'Other Times' : 'Submit Availability'}
                          </button>
                        </div>
                      </div>
                      <div className="px-4 pb-2 text-xs text-gray-400">{activeConv?.clientName} · {timeAgo(msg.created_at)}</div>
                    </div>
                  </div>
                );
              }
              if (isPrescreenAvail && msg.sender_type === 'consultant') {
                const slots = msg.metadata?.slots || [];
                return (
                  <div key={msg.id} className="flex justify-end">
                    <div className="max-w-xs rounded-2xl border border-teal-300/40 overflow-hidden bg-teal-50">
                      <div className="px-4 pt-3 pb-1">
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle className="w-4 h-4 text-teal-500" />
                          <span className="text-sm font-semibold text-gray-800">Availability Submitted</span>
                        </div>
                        <div className="space-y-1 mb-2">
                          {slots.map((s, i) => (
                            <div key={i} className="text-xs px-2.5 py-1.5 rounded-lg bg-white/60 text-gray-700">
                              {new Date(s.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} at {s.time}
                            </div>
                          ))}
                        </div>
                        <p className="text-xs pb-2 text-gray-400">You · {timeAgo(msg.created_at)}</p>
                      </div>
                    </div>
                  </div>
                );
              }
              if (isPrescreenConfirmed) {
                const slot = msg.metadata?.confirmed_slot;
                return (
                  <div key={msg.id} className="flex justify-start">
                    <div className="max-w-xs rounded-2xl border-2 border-emerald-300/50 overflow-hidden bg-white shadow-sm">
                      <div className="bg-emerald-50 px-4 py-3">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-600" />
                          <span className="text-sm font-bold text-emerald-800">Interview Confirmed!</span>
                        </div>
                        {slot && <p className="text-sm font-semibold text-emerald-700 mt-1">{new Date(slot.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} at {slot.time}</p>}
                        <p className="text-xs text-gray-400 mt-0.5">{activeConv?.clientName} · {timeAgo(msg.created_at)}</p>
                      </div>
                    </div>
                  </div>
                );
              }
              return (
                <div key={msg.id} className={`flex ${msg.sender_type === 'consultant' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-xl px-4 py-3 ${msg.sender_type === 'consultant' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-800'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold opacity-80">{msg.sender_type === 'consultant' ? 'You' : activeConv.clientName}</span>
                      <span className="text-xs opacity-60">{timeAgo(msg.created_at)}</span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{msg.message_body}</p>
                  </div>
                </div>
              );
            })}
            <div ref={endRef} />
          </div>

          {/* Reply box */}
          <div className="p-4 border-t border-gray-200">
            {activeConv.canReply ? (
              <div className="flex gap-2">
                <textarea value={reply} onChange={e => setReply(e.target.value)}
                  placeholder={`Reply to ${activeConv.clientName}…`} rows={2}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleReply(); } }}
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                <button onClick={handleReply} disabled={sending || !reply.trim()}
                  className="self-end flex items-center justify-center w-9 h-9 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex-shrink-0">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2.5 p-3 rounded-lg bg-gray-50 text-sm text-gray-500">
                <AlertCircle className="w-4 h-4 flex-shrink-0 text-gray-400" />
                You can reply once this company sends you a message first.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Inbox list — exact AdminMessages row pattern */}
      {mode === 'list' && (
        <>
          {convs.length === 0 ? (
            <div className="p-8 text-center">
              <Mail className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 text-sm mb-1">No messages yet</p>
              <p className="text-xs text-gray-400">When a Company reaches out, their message will appear here.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {convs.map(conv => {
                const isUnread = conv.unread > 0;
                const preview = conv.lastMsg
                  ? (conv.lastMsg.sender_type === 'consultant' ? 'You: ' : `${conv.clientName}: `) + conv.lastMsg.message_body
                  : '';
                return (
                  <div key={conv.clientId} className={isUnread ? 'bg-blue-50' : 'bg-white'}>
                    <div className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => openThread(conv)}>
                      {/* Unread dot */}
                      <div className="w-2 flex-shrink-0 flex justify-center">
                        {isUnread && <span className="w-2 h-2 bg-blue-600 rounded-full block" />}
                      </div>
                      {/* Icon */}
                      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-teal-100">
                        <MessageSquare className="w-4 h-4 text-teal-600" />
                      </div>
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm truncate ${isUnread ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>
                            {conv.clientName}
                          </span>
                          {conv.unread > 0 && (
                            <span className="flex-shrink-0 w-5 h-5 bg-blue-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                              {conv.unread}
                            </span>
                          )}
                          {!conv.canReply && (
                            <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-400">awaiting</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 truncate mt-0.5">{preview}</p>
                      </div>
                      {/* Date */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {conv.lastMsg && <span className="text-xs text-gray-400">{timeAgo(conv.lastMsg.created_at)}</span>}
                        <ChevronRight className="w-4 h-4 text-gray-300" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div className="mx-4 my-3 flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-100 text-xs text-blue-600">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-blue-500" />
            <span>Customers can message you through their Pipeline feature. You may reply once they initiate contact.</span>
          </div>
        </>
      )}

      {/* Pre-screen scheduling modal */}
      {scheduleModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[300] p-4" onClick={() => { setScheduleModal(null); }}>
          <div className="max-w-md w-full rounded-2xl shadow-2xl overflow-hidden bg-white text-gray-900" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-blue-600 to-teal-400 p-5 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold">{scheduleMode === 'accept' ? 'Accept a Time Slot' : 'Propose Your Availability'}</h2>
                    <p className="text-white/80 text-xs mt-0.5">{scheduleModal.metadata?.company_name} · {scheduleModal.metadata?.job_title}</p>
                  </div>
                </div>
                <button onClick={() => setScheduleModal(null)} className="p-2 hover:bg-white/20 rounded-lg transition-colors"><X className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="p-5">
              {scheduleMode === 'accept' && (() => {
                const slots = scheduleModal.metadata?.proposed_slots || [];
                return (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600">Select the time that works best for you.</p>
                    <div className="space-y-2">
                      {slots.map((s, i) => {
                        const isSelected = acceptedSlot?.date === s.date && acceptedSlot?.time === s.time;
                        return (
                          <button key={i} onClick={() => setAcceptedSlot(s)}
                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-left transition-all ${isSelected ? 'border-teal-400 bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`}>
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'border-teal-400 bg-teal-400' : 'border-gray-300'}`}>
                              {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-gray-800">{new Date(s.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
                              <p className="text-xs text-gray-500">{s.time}</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button onClick={() => setScheduleMode('propose')} className="flex-1 py-2.5 rounded-xl text-sm font-medium border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors">None work — propose other times</button>
                      <button onClick={submitAvailability} disabled={submittingAvail || !acceptedSlot}
                        className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-teal-400 text-black hover:bg-teal-400/90 transition-colors disabled:opacity-50">
                        {submittingAvail ? 'Confirming...' : 'Confirm This Time'}
                      </button>
                    </div>
                  </div>
                );
              })()}
              {scheduleMode === 'propose' && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">Provide up to 3 times when you're available.</p>
                  {availSlots.map((slot, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <span className="text-xs font-semibold w-16 flex-shrink-0 text-gray-500">Option {i + 1}</span>
                      <input type="date" value={slot.date} onChange={e => setAvailSlots(prev => prev.map((s, j) => j === i ? { ...s, date: e.target.value } : s))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-400 focus:border-transparent outline-none" />
                      <input type="time" value={slot.time} onChange={e => setAvailSlots(prev => prev.map((s, j) => j === i ? { ...s, time: e.target.value } : s))}
                        className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-400 focus:border-transparent outline-none" />
                    </div>
                  ))}
                  <div className="flex gap-2 pt-1">
                    {(scheduleModal.metadata?.proposed_slots || []).length > 0 && (
                      <button onClick={() => setScheduleMode('accept')} className="py-2.5 px-4 rounded-xl text-sm font-medium border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors">Back</button>
                    )}
                    <button onClick={() => setScheduleModal(null)} className="flex-1 py-2.5 rounded-xl text-sm font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors">Cancel</button>
                    <button onClick={submitAvailability} disabled={submittingAvail || availSlots.every(s => !s.date || !s.time)}
                      className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-teal-400 text-black hover:bg-teal-400/90 transition-colors disabled:opacity-50">
                      {submittingAvail ? 'Sending...' : 'Send My Availability'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function Opportunities() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { theme, setThemeTransient } = useTheme();
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isLoadingLogo, setIsLoadingLogo] = useState(true);

  const [activeTab, setActiveTab] = useState<PageTab>('opportunities');

  // Opportunities state
  const [opportunities, setOpportunities] = useState<JobPosting[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [selectedJob, setSelectedJob] = useState<JobPosting | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Applications state
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [appLoading, setAppLoading] = useState(false);
  const [appSearch, setAppSearch] = useState('');
  const [appFilter, setAppFilter] = useState<AppFilter>('all');
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [expandedApp, setExpandedApp] = useState<string | null>(null);

  // Messaging sub-tab state (inside "applications" tab)
  type AppSubTab = 'list' | 'messaging';
  type MsgViewState = 'list' | 'thread';
  const [appSubTab, setAppSubTab] = useState<AppSubTab>('list');
  const [conversations, setConversations] = useState<ClientConv[]>([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [activeConv, setActiveConv] = useState<ClientConv | null>(null);
  const [threadMsgs, setThreadMsgs] = useState<CandidateMessage[]>([]);
  const [msgView, setMsgView] = useState<MsgViewState>('list');
  const [msgDraft, setMsgDraft] = useState('');
  const [msgSending, setMsgSending] = useState(false);
  const threadEndRef = useRef<HTMLDivElement>(null);
  // Prescreen scheduling (messages sub-tab)
  const [scheduleModal, setScheduleModal] = useState<CandidateMessage | null>(null);
  const [scheduleMode, setScheduleMode] = useState<'accept' | 'propose'>('accept');
  const [acceptedSlot, setAcceptedSlot] = useState<{ date: string; time: string } | null>(null);
  const [availSlots, setAvailSlots] = useState([{ date: '', time: '' }, { date: '', time: '' }, { date: '', time: '' }]);
  const [submittingAvail, setSubmittingAvail] = useState(false);

  const isDark = theme === 'dark';

  useEffect(() => { setThemeTransient('light'); }, [setThemeTransient]);

  useEffect(() => {
    fetchLogo();
    fetchOpportunities();
    const ch = supabase.channel('job_postings_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'workday_client_job_postings' }, fetchOpportunities)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [user?.id]);

  useEffect(() => {
    if (activeTab === 'applications' && user) {
      loadApplications();
      loadFavorites();
      loadConversations();
    }
  }, [activeTab, user]);

  useEffect(() => {
    if (!user) return;
    const ch = supabase.channel('opp-page-msgs-' + user.id)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'candidate_messages', filter: `consultant_id=eq.${user.id}` }, () => {
        loadConversations();
        if (activeConv) loadThread(activeConv.clientId);
      }).subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [user?.id, activeConv?.clientId]);

  useEffect(() => {
    if (threadEndRef.current && msgView === 'thread') threadEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [threadMsgs, msgView]);

  const fetchLogo = async () => {
    setIsLoadingLogo(true);
    setLogoUrl('https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=200&h=80&fit=crop&auto=format');
    const { data } = await supabase.from('settings').select('value').eq('key', 'logo_url').maybeSingle();
    if (data?.value) {
      const img = new Image();
      img.onload = () => { setLogoUrl(data.value); setIsLoadingLogo(false); };
      img.onerror = () => setIsLoadingLogo(false);
      img.src = data.value;
    } else {
      setIsLoadingLogo(false);
    }
  };

  const fetchOpportunities = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('workday_client_job_postings')
        .select(`*, workday_client_profiles!workday_client_job_postings_customer_id_fkey(company_id, background_image_url, company_profile_image_url, companies(name))`)
        .eq('status', 'open')
        .order('created_at', { ascending: false });
      if (error) throw error;
      const STORAGE_BASE = `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/profile-photos/`;
      setOpportunities((data || []).map(job => {
        const profile = job.workday_client_profiles as any;
        const bgPath = profile?.background_image_url;
        const logoPath = profile?.company_profile_image_url;
        return {
          ...job,
          company_name: job.is_pro_posting ? (profile?.companies?.name || 'Company') : null,
          background_image_url: bgPath ? `${STORAGE_BASE}${bgPath}` : null,
          company_profile_image_url: logoPath ? `${STORAGE_BASE}${logoPath}` : null,
        };
      }));
    } catch (err) {
      console.error('Error fetching opportunities:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadApplications = async () => {
    if (!user) return;
    setAppLoading(true);
    const { data: apps } = await supabase
      .from('job_applications')
      .select('id, job_posting_id, status, applied_at, cover_letter, resume_url, question_answers')
      .eq('consultant_id', user.id)
      .order('applied_at', { ascending: false });
    if (!apps) { setAppLoading(false); return; }

    const postingIds = [...new Set(apps.map(a => a.job_posting_id))];
    const { data: postings } = postingIds.length > 0
      ? await supabase
          .from('workday_client_job_postings')
          .select(`id, job_title, worker_type, hire_deadline_date, is_pro_posting, workday_client_profiles!workday_client_job_postings_customer_id_fkey(companies(name))`)
          .in('id', postingIds)
      : { data: [] };

    const pmap = new Map((postings || []).map((p: any) => [p.id, p]));
    setApplications(apps.map(app => {
      const p = pmap.get(app.job_posting_id) as any;
      return {
        ...app,
        job_title: p?.job_title || 'Workday Position',
        worker_type: p?.worker_type,
        hire_deadline_date: p?.hire_deadline_date,
        is_pro_posting: p?.is_pro_posting || false,
        company_name: p?.is_pro_posting ? p?.workday_client_profiles?.companies?.name : null,
        is_complete: !!(app.cover_letter && app.resume_url),
      };
    }));
    setAppLoading(false);
  };

  const loadFavorites = async () => {
    if (!user) return;
    const { data } = await supabase.from('consultant_application_favorites').select('application_id').eq('consultant_id', user.id);
    setFavorites(new Set(data?.map(r => r.application_id) || []));
  };

  const toggleFavorite = async (appId: string) => {
    if (!user) return;
    const isFav = favorites.has(appId);
    if (isFav) {
      await supabase.from('consultant_application_favorites').delete().eq('consultant_id', user.id).eq('application_id', appId);
      setFavorites(prev => { const n = new Set(prev); n.delete(appId); return n; });
    } else {
      await supabase.from('consultant_application_favorites').upsert({ consultant_id: user.id, application_id: appId }, { onConflict: 'consultant_id,application_id' });
      setFavorites(prev => new Set([...prev, appId]));
    }
  };

  // ── Messaging functions ──────────────────────────────────────────────────────

  const loadConversations = async () => {
    if (!user) return;
    setMsgLoading(true);
    const { data: msgs } = await supabase.from('candidate_messages').select('*').eq('consultant_id', user.id).order('created_at', { ascending: false });
    if (!msgs) { setMsgLoading(false); return; }
    const clientIds = [...new Set(msgs.map(m => m.client_id))];
    const { data: profiles } = await supabase.from('workday_client_profiles').select('id, first_name, last_name, photo_url').in('id', clientIds);
    const convMap = new Map<string, ClientConv>();
    for (const msg of msgs) {
      if (!convMap.has(msg.client_id)) {
        const p = profiles?.find((x: any) => x.id === msg.client_id);
        convMap.set(msg.client_id, {
          clientId: msg.client_id,
          clientName: p ? `${p.first_name} ${p.last_name}` : 'Client',
          clientPhotoUrl: p?.photo_url || undefined,
          lastMsg: msg, unread: 0, canReply: false,
        });
      }
      if (msg.sender_type === 'client' && !msg.is_read) convMap.get(msg.client_id)!.unread++;
    }
    for (const [clientId, conv] of convMap.entries()) {
      conv.canReply = msgs.some(m => m.client_id === clientId && m.sender_type === 'client');
    }
    setConversations([...convMap.values()]);
    setMsgLoading(false);
  };

  const loadThread = async (clientId: string) => {
    if (!user) return;
    const { data } = await supabase.from('candidate_messages').select('*').eq('client_id', clientId).eq('consultant_id', user.id).order('created_at', { ascending: true });
    setThreadMsgs(data || []);
    for (const m of (data || []).filter((m: CandidateMessage) => m.sender_type === 'client' && !m.is_read)) {
      await supabase.from('candidate_messages').update({ is_read: true }).eq('id', m.id);
    }
    loadConversations();
  };

  const openThread = (conv: ClientConv) => {
    setActiveConv(conv);
    loadThread(conv.clientId);
    setMsgView('thread');
  };

  const sendMessage = async () => {
    if (!msgDraft.trim() || !activeConv || !user || !activeConv.canReply) return;
    setMsgSending(true);
    await supabase.from('candidate_messages').insert({
      client_id: activeConv.clientId, consultant_id: user.id,
      sender_type: 'consultant', message_body: msgDraft.trim(),
    });
    setMsgDraft('');
    loadThread(activeConv.clientId);
    setMsgSending(false);
  };

  const submitAvailability = async () => {
    if (!scheduleModal || !activeConv || !user) return;
    setSubmittingAvail(true);
    const appId = scheduleModal.metadata?.application_id;
    const jobTitle = scheduleModal.metadata?.job_title || 'the position';
    if (scheduleMode === 'accept' && acceptedSlot) {
      const label = `${new Date(acceptedSlot.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} at ${acceptedSlot.time}`;
      await supabase.from('candidate_messages').insert({
        client_id: activeConv.clientId, consultant_id: user.id, sender_type: 'consultant',
        message_body: `I can make the pre-screen interview for ${jobTitle} on ${label}.`,
        message_type: 'prescreen_availability',
        metadata: { application_id: appId, slots: [acceptedSlot], accepted_slot: acceptedSlot },
      });
    } else {
      const validSlots = availSlots.filter(s => s.date && s.time);
      if (validSlots.length === 0) { setSubmittingAvail(false); return; }
      const slotLabels = validSlots.map(s => `• ${new Date(s.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} at ${s.time}`).join('\n');
      await supabase.from('candidate_messages').insert({
        client_id: activeConv.clientId, consultant_id: user.id, sender_type: 'consultant',
        message_body: `My availability for the Pre-Screen Interview (${jobTitle}):\n${slotLabels}`,
        message_type: 'prescreen_availability',
        metadata: { application_id: appId, slots: validSlots },
      });
    }
    if (appId) {
      await supabase.from('job_applications').update({ prescreen_status: 'availability_submitted' }).eq('id', appId);
      setApplications(prev => prev.map(a => a.id === appId ? { ...a, prescreen_status: 'availability_submitted' } : a));
    }
    setScheduleModal(null); setScheduleMode('accept'); setAcceptedSlot(null);
    setAvailSlots([{ date: '', time: '' }, { date: '', time: '' }, { date: '', time: '' }]);
    loadThread(activeConv.clientId);
    setSubmittingAvail(false);
  };

  const filteredOpportunities = opportunities.filter(opp => {
    const q = searchTerm.toLowerCase();
    const matchesSearch = !q ||
      opp.job_title?.toLowerCase().includes(q) ||
      opp.job_description?.toLowerCase().includes(q) ||
      opp.company_name?.toLowerCase().includes(q) ||
      opp.requisition_number?.toLowerCase().includes(q);
    const matchesFilter = filterType === 'all' || opp.worker_type?.toLowerCase() === filterType;
    return matchesSearch && matchesFilter;
  });

  const incompleteCount = applications.filter(a => !a.is_complete).length;

  const filteredApps = applications.filter(app => {
    const q = appSearch.toLowerCase();
    const matchesSearch = !q || app.job_title?.toLowerCase().includes(q) || (app.company_name?.toLowerCase().includes(q) ?? false);
    if (appFilter === 'all') return matchesSearch;
    if (appFilter === 'incomplete') return matchesSearch && !app.is_complete;
    // pre_selected maps to DB value 'shortlisted'
    const statusToMatch = appFilter === 'pre_selected' ? 'shortlisted' : appFilter;
    return matchesSearch && app.status === statusToMatch;
  });

  const card = `rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`;

  return (
    <div className={`min-h-screen transition-colors duration-200 ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <JobDetailModal
        job={selectedJob}
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setSelectedJob(null); fetchOpportunities(); }}
        theme={theme}
      />

      {/* Header */}
      <nav className="fixed w-full z-50 bg-black backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            <div className="text-white -mt-4 -mb-4 cursor-default select-none">
              {isLoadingLogo ? (
                <div className="h-32 md:h-28 w-[200px] flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary" />
                </div>
              ) : (
                <img src="/1.png" alt="PerplexityATS" className="h-40 w-auto object-contain"
                  onError={e => { e.currentTarget.style.display = 'none'; }} />
              )}
            </div>
            <button onClick={() => navigate('/consultant/dashboard')}
              className="text-white hover:text-primary transition-colors flex items-center gap-2">
              <ArrowLeft className="w-5 h-5" />Back to Dashboard
            </button>
          </div>
        </div>
      </nav>

      <main className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">

          {/* Page title + tab switcher */}
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
            <div>
              <h1 className="text-4xl font-bold mb-1">Workday Opportunities</h1>
              <p className={`text-base ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Browse positions and track your applications
              </p>
            </div>

            <div className={`flex gap-1 p-1 rounded-xl ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
              <button onClick={() => setActiveTab('opportunities')}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === 'opportunities' ? 'bg-primary text-black shadow-sm' : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'
                }`}>
                <Briefcase className="w-4 h-4" />
                Opportunities
                {opportunities.length > 0 && (
                  <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${activeTab === 'opportunities' ? 'bg-black/20' : isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                    {opportunities.length}
                  </span>
                )}
              </button>
              <button onClick={() => setActiveTab('applications')}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === 'applications' ? 'bg-primary text-black shadow-sm' : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'
                }`}>
                <MessageSquare className="w-4 h-4" />
                Job Applications &amp; Messaging
                {incompleteCount > 0 && (
                  <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${activeTab === 'applications' ? 'bg-black/20' : 'bg-orange-100 text-orange-700'}`}>
                    {incompleteCount}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* ── OPPORTUNITIES TAB ── */}
          {activeTab === 'opportunities' && (
            <>
              <div className="mb-8 flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input type="text" placeholder="Search opportunities…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                    className={`w-full pl-10 pr-4 py-3 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-primary`} />
                </div>
                <div className="relative">
                  <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select value={filterType} onChange={e => setFilterType(e.target.value)}
                    className={`pl-10 pr-8 py-3 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-primary`}>
                    <option value="all">All Types</option>
                    <option value="hire">Hire</option>
                    <option value="contract">Contract</option>
                  </select>
                </div>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary" />
                </div>
              ) : filteredOpportunities.length === 0 ? (
                <div className={`text-center py-12 rounded-xl ${isDark ? 'bg-gray-800' : 'bg-white'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  <Briefcase className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                  <h3 className="text-xl font-semibold mb-2">No opportunities found</h3>
                  <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>Try adjusting your search or filters</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {filteredOpportunities.map(opp => (
                    <div key={opp.id}
                      className={`rounded-xl overflow-hidden transition-all duration-200 hover:scale-[1.01] hover:shadow-md ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200 shadow-sm'}`}>

                      {/* Branding banner — only shown for Pro (direct) postings; Perplexity-managed postings hide client branding */}
                      {opp.is_pro_posting && (opp.background_image_url || opp.company_profile_image_url) && (
                        <div className="relative">
                          {/* Cover photo */}
                          <div className="w-full overflow-hidden" style={{ paddingTop: 'calc(191 / 1584 * 100%)', position: 'relative' }}>
                            {opp.background_image_url ? (
                              <img src={opp.background_image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                            ) : (
                              <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-r from-gray-700 to-gray-600' : 'bg-gradient-to-r from-slate-200 to-slate-300'}`} />
                            )}
                          </div>
                          {/* Logo overlapping the cover */}
                          {opp.company_profile_image_url && (
                            <div className="absolute left-4 bottom-0 translate-y-1/2">
                              <div className={`w-12 h-12 rounded-xl border-2 overflow-hidden shadow-md ${isDark ? 'border-gray-800 bg-gray-700' : 'border-white bg-gray-100'}`}>
                                <img src={opp.company_profile_image_url} alt="Logo" className="w-full h-full object-cover" />
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      <div className={`p-5 ${opp.is_pro_posting && (opp.background_image_url || opp.company_profile_image_url) ? 'pt-8' : ''}`}>
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1.5">
                              <h3 className="text-lg font-bold">{opp.job_title || opp.job_family || 'Workday Position'}</h3>
                              {opp.is_pro_posting && (
                                <span className="flex items-center gap-1 px-2 py-0.5 bg-primary/20 text-primary text-xs font-bold rounded-full border border-primary/30">
                                  <Star className="w-3 h-3" />Pro
                                </span>
                              )}
                            </div>
                            <div className={`flex items-center text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              <Building2 className="w-4 h-4 mr-2 flex-shrink-0" />
                              {opp.is_pro_posting && opp.company_name ? (
                                <span className="font-medium">{opp.company_name}</span>
                              ) : (
                                <span className="flex items-center gap-1.5 italic text-gray-400">
                                  <EyeOff className="w-3.5 h-3.5" />Company name hidden
                                </span>
                              )}
                            </div>
                            {opp.requisition_number && (
                              <div className={`flex items-center text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                <Briefcase className="w-4 h-4 mr-2" />Req #: {opp.requisition_number}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                            <div className="bg-primary text-black px-3 py-1 rounded-full text-sm font-semibold capitalize">
                              {opp.worker_type || 'Contract'}
                            </div>
                            <DotMenu items={[
                              { label: 'View Details & Apply', icon: <ExternalLink className="w-3.5 h-3.5" />, onClick: () => { setSelectedJob(opp); setIsModalOpen(true); } },
                              { label: 'View My Applications', icon: <Briefcase className="w-3.5 h-3.5" />, onClick: () => setActiveTab('applications') },
                            ]} />
                          </div>
                        </div>

                        {/* Quick-post indicator */}
                        {opp.career_page_url && (
                          <div className={`flex items-center gap-2 mb-3 px-3 py-2 rounded-lg text-xs font-medium ${isDark ? 'bg-teal-900/30 text-teal-300 border border-teal-800' : 'bg-teal-50 text-teal-700 border border-teal-200'}`}>
                            <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
                            Apply directly through the company's Workday portal
                          </div>
                        )}

                        <p className={`mb-4 line-clamp-3 text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          {opp.job_description}
                        </p>

                        <div className="space-y-1.5 mb-4">
                          {opp.job_family && (
                            <div className={`flex items-center text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                              <Users className="w-4 h-4 mr-2 text-primary" />{opp.job_family}
                            </div>
                          )}
                          {opp.compensation_amount > 0 && (
                            <div className={`flex items-center text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                              <DollarSign className="w-4 h-4 mr-2 text-primary" />${opp.compensation_amount.toLocaleString()}/year
                            </div>
                          )}
                          {opp.hire_deadline_date && (
                            <div className={`flex items-center text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                              <Calendar className="w-4 h-4 mr-2 text-primary" />Deadline: {new Date(opp.hire_deadline_date).toLocaleDateString()}
                            </div>
                          )}
                        </div>

                        <button onClick={() => { setSelectedJob(opp); setIsModalOpen(true); }}
                          className="w-full bg-primary text-black font-semibold py-2.5 rounded-lg hover:bg-primary/90 transition-colors text-sm">
                          View Details &amp; Apply
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ── JOB APPLICATIONS & MESSAGING TAB ── */}
          {activeTab === 'applications' && (
            <div className="space-y-5">

              {/* Sub-tab switcher */}
              <div className={`flex gap-1 p-1 rounded-xl w-fit ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <button onClick={() => setAppSubTab('list')}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${appSubTab === 'list' ? 'bg-primary text-black shadow-sm' : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'}`}>
                  <Briefcase className="w-4 h-4" />Applications
                  {incompleteCount > 0 && (
                    <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${appSubTab === 'list' ? 'bg-black/20' : 'bg-orange-100 text-orange-700'}`}>{incompleteCount}</span>
                  )}
                </button>
                <button onClick={() => { setAppSubTab('messaging'); loadConversations(); }}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${appSubTab === 'messaging' ? 'bg-primary text-black shadow-sm' : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'}`}>
                  <MessageSquare className="w-4 h-4" />Messages
                  {conversations.reduce((s, c) => s + c.unread, 0) > 0 && (
                    <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${appSubTab === 'messaging' ? 'bg-black/20' : 'bg-primary text-black'}`}>{conversations.reduce((s, c) => s + c.unread, 0)}</span>
                  )}
                </button>
              </div>

              {/* ── APPLICATIONS SUB-TAB ── */}
              {appSubTab === 'list' && (
              <div className="space-y-5">

                {/* Search + filter */}
                <div className={`p-4 rounded-xl shadow-sm border ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
                  <div className="flex items-center gap-3 flex-wrap mb-3">
                    <div className="flex-1 relative min-w-[180px]">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input type="text" placeholder="Search applications…" value={appSearch} onChange={e => setAppSearch(e.target.value)}
                        className={`w-full pl-9 pr-4 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'} focus:ring-2 focus:ring-primary focus:border-transparent outline-none`} />
                    </div>
                    <button onClick={loadApplications}
                      className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                      <RefreshCw className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {(['all', 'pending', 'pre_selected', 'interview_scheduled', 'hired', 'rejected', 'incomplete'] as AppFilter[]).map(f => (
                      <button key={f} onClick={() => setAppFilter(f)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${appFilter === f ? 'bg-primary text-black border-primary' : isDark ? 'border-gray-700 text-gray-400 hover:text-white' : 'border-gray-200 text-gray-500 hover:text-gray-800'}`}>
                        {f === 'all' ? 'All' :
                         f === 'incomplete' ? `Incompleted Application (${incompleteCount})` :
                         f === 'rejected' ? 'Filled with another Candidate' :
                         f === 'hired' ? 'Offer' :
                         f === 'reviewed' ? 'Pre-Screened' :
                         f === 'pre_selected' ? 'Pre-Selected' :
                         f === 'interview_scheduled' ? 'Interview' :
                         f.charAt(0).toUpperCase() + f.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: 'Total Applied', value: applications.length, color: 'text-primary' },
                    { label: 'In Progress', value: applications.filter(a => ['pending','reviewed','shortlisted','pre_selected','interview_scheduled'].includes(a.status)).length, color: 'text-blue-500' },
                    { label: 'Incompleted Application', value: incompleteCount, color: 'text-orange-500' },
                    { label: 'Favorited', value: favorites.size, color: 'text-red-500' },
                  ].map(s => (
                    <div key={s.label} className={card + ' p-4'}>
                      <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
                      <div className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{s.label}</div>
                    </div>
                  ))}
                </div>

                {/* Applications list */}
                {appLoading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary" />
                  </div>
                ) : filteredApps.length === 0 ? (
                  <div className={`text-center py-12 rounded-xl ${isDark ? 'bg-gray-800' : 'bg-white'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <Briefcase className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <h3 className="font-semibold mb-1">No applications found</h3>
                    <p className={`text-sm mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      {appFilter !== 'all' ? 'Try changing the filter above.' : 'Start by browsing open opportunities.'}
                    </p>
                    <button onClick={() => setActiveTab('opportunities')}
                      className="px-4 py-2 bg-primary text-black rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
                      Browse Opportunities
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredApps.map(app => {
                      const isFav = favorites.has(app.id);
                      const cfg = APP_STATUS[app.status] || { label: app.status, badge: 'bg-gray-100 text-gray-700' };
                      const isExpanded = expandedApp === app.id;
                      return (
                        <div key={app.id} className={`${card} overflow-hidden`}>
                          <div className="flex items-start gap-3 p-4">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                              <Briefcase className="w-5 h-5 text-primary" />
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  {/* Title row */}
                                  <div className="flex items-center gap-2 flex-wrap mb-1">
                                    <h3 className="text-sm font-semibold truncate">{app.job_title}</h3>
                                    {app.is_pro_posting && (
                                      <span className="flex items-center gap-1 px-2 py-0.5 bg-primary/20 text-primary text-xs font-bold rounded-full border border-primary/30">
                                        <Star className="w-3 h-3" />Pro
                                      </span>
                                    )}
                                    {!app.is_complete && (
                                      <span className="flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-semibold rounded-full">
                                        <AlertCircle className="w-3 h-3" />Incompleted Application
                                      </span>
                                    )}
                                  </div>
                                  {/* Meta row */}
                                  <div className={`flex items-center gap-2 text-xs flex-wrap ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    <span className="flex items-center gap-1">
                                      <Building2 className="w-3 h-3" />
                                      {app.is_pro_posting && app.company_name ? app.company_name : (
                                        <span className="flex items-center gap-1 italic text-gray-400"><EyeOff className="w-3 h-3" />Hidden</span>
                                      )}
                                    </span>
                                    {app.worker_type && <span className="capitalize">· {app.worker_type}</span>}
                                    <span className={isDark ? 'text-gray-600' : 'text-gray-400'}>· Applied {timeAgo(app.applied_at)}</span>
                                  </div>
                                  {app.hire_deadline_date && (
                                    <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                      Deadline: {new Date(app.hire_deadline_date).toLocaleDateString()}
                                    </p>
                                  )}
                                </div>

                                {/* Right side actions */}
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.badge}`}>{cfg.label}</span>
                                  <button onClick={() => toggleFavorite(app.id)} className="p-1 transition-colors">
                                    <Heart className={`w-4 h-4 transition-all ${isFav ? 'fill-red-500 text-red-500' : 'text-gray-300 hover:text-red-400'}`} />
                                  </button>
                                  <DotMenu items={[
                                    { label: 'View Job Posting', icon: <ExternalLink className="w-3.5 h-3.5" />, onClick: () => setActiveTab('opportunities') },
                                    { label: 'Complete Application', icon: <CheckCircle className="w-3.5 h-3.5" />, onClick: () => setActiveTab('opportunities') },
                                    { label: isExpanded ? 'Collapse Details' : 'Expand Details', icon: <ChevronDown className="w-3.5 h-3.5" />, onClick: () => setExpandedApp(isExpanded ? null : app.id) },
                                    { label: isFav ? 'Remove Favorite' : 'Add to Favorites', icon: <Heart className="w-3.5 h-3.5" />, onClick: () => toggleFavorite(app.id) },
                                  ]} />
                                  <button onClick={() => setExpandedApp(isExpanded ? null : app.id)}
                                    className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                                    {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                                  </button>
                                </div>
                              </div>

                              {/* Incomplete warning */}
                              {!app.is_complete && (
                                <div className={`mt-3 flex items-start gap-2 p-3 rounded-lg ${isDark ? 'bg-orange-900/20 border border-orange-800/40' : 'bg-orange-50 border border-orange-200'}`}>
                                  <AlertCircle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <p className="text-xs font-semibold text-orange-600">Incompleted Application</p>
                                    <p className={`text-xs mt-0.5 ${isDark ? 'text-orange-400' : 'text-orange-500'}`}>
                                      {!app.resume_url && !app.cover_letter ? 'Missing resume and cover letter.' :
                                       !app.resume_url ? 'Missing resume.' : 'Missing cover letter.'}
                                      {' '}
                                      <button onClick={() => setActiveTab('opportunities')} className="underline hover:no-underline">
                                        Complete it now
                                      </button>
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Expanded details */}
                          {isExpanded && (
                            <div className={`px-4 pb-4 pt-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {app.cover_letter && (
                                  <div>
                                    <p className={`text-xs font-semibold mb-1.5 uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Cover Letter</p>
                                    <p className={`text-sm leading-relaxed line-clamp-5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{app.cover_letter}</p>
                                  </div>
                                )}
                                {app.question_answers && app.question_answers.length > 0 && (
                                  <div>
                                    <p className={`text-xs font-semibold mb-1.5 uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Your Answers</p>
                                    <div className="space-y-2">
                                      {app.question_answers.map((qa, i) => (
                                        <div key={i}>
                                          <p className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{qa.question}</p>
                                          <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{qa.answer}</p>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                              {app.resume_url && (
                                <a href={app.resume_url} target="_blank" rel="noopener noreferrer"
                                  className="mt-3 inline-flex items-center gap-1.5 text-primary text-sm hover:underline">
                                  <ExternalLink className="w-3.5 h-3.5" />View Resume
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              )}

              {/* ── MESSAGING SUB-TAB ── */}
              {appSubTab === 'messaging' && (
                <div className="max-w-2xl mx-auto">
                  {msgView === 'list' ? (
                    <div className={card}>
                      <div className={`px-4 py-3 border-b flex items-center justify-between ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                        <div className="flex items-center gap-2">
                          <Inbox className="w-5 h-5 text-primary" />
                          <h2 className="font-semibold">Messages from Hiring Companies</h2>
                          {conversations.reduce((s, c) => s + c.unread, 0) > 0 && (
                            <span className="px-2 py-0.5 bg-primary text-black text-xs font-bold rounded-full">{conversations.reduce((s, c) => s + c.unread, 0)}</span>
                          )}
                        </div>
                        <button onClick={loadConversations} className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                          <RefreshCw className="w-4 h-4 text-gray-400" />
                        </button>
                      </div>
                      <div className={`mx-4 mt-3 mb-1 flex items-start gap-2.5 p-3 rounded-lg text-xs ${isDark ? 'bg-blue-900/20 border border-blue-800/30 text-blue-300' : 'bg-blue-50 border border-blue-100 text-blue-700'}`}>
                        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>You can reply to clients who have messaged you first. Clients on Pro plans may reach out through the Pipeline & Tracking feature.</span>
                      </div>
                      {msgLoading ? (
                        <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary" /></div>
                      ) : conversations.length === 0 ? (
                        <div className="text-center py-12">
                          <Mail className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                          <h3 className="font-semibold mb-1">No messages yet</h3>
                          <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>When a client reaches out, their message will appear here.</p>
                        </div>
                      ) : (
                        <div className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-100'}`}>
                          {conversations.map(conv => (
                            <div key={conv.clientId} onClick={() => openThread(conv)}
                              className={`flex items-center gap-3 px-4 py-3.5 cursor-pointer transition-colors ${conv.unread > 0 ? (isDark ? 'bg-primary/5' : 'bg-primary/5') : ''} ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                              <div className="relative">
                                <Avatar name={conv.clientName} photoUrl={conv.clientPhotoUrl} size="md" />
                                {conv.unread > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-black text-xs font-bold rounded-full flex items-center justify-center">{conv.unread}</span>}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className={`text-sm truncate ${conv.unread > 0 ? 'font-semibold' : 'font-medium'}`}>{conv.clientName}</p>
                                  {!conv.canReply && <span className={`text-xs px-1.5 py-0.5 rounded-full ${isDark ? 'bg-gray-700 text-gray-500' : 'bg-gray-100 text-gray-400'}`}>Read only</span>}
                                </div>
                                {conv.lastMsg && <p className={`text-xs truncate mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{conv.lastMsg.sender_type === 'consultant' ? 'You: ' : ''}{conv.lastMsg.message_body}</p>}
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                {conv.lastMsg && <span className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{timeAgo(conv.lastMsg.created_at)}</span>}
                                <ChevronRight className="w-4 h-4 text-gray-300" />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Thread view */
                    <div className={card}>
                      <div className={`px-4 py-3 border-b flex items-center gap-3 ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                        <button onClick={() => { setMsgView('list'); setActiveConv(null); setThreadMsgs([]); }} className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                          <ArrowLeft className="w-4 h-4 text-gray-400" />
                        </button>
                        {activeConv && <Avatar name={activeConv.clientName} photoUrl={activeConv.clientPhotoUrl} size="sm" />}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm">{activeConv?.clientName}</p>
                          {activeConv && !activeConv.canReply && <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Waiting for client to message you first</p>}
                        </div>
                      </div>

                      <div className="overflow-y-auto p-4 space-y-3" style={{ minHeight: 320, maxHeight: 480 }}>
                        {threadMsgs.length === 0 ? (
                          <div className="text-center py-8">
                            <MessageSquare className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                            <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No messages yet</p>
                          </div>
                        ) : threadMsgs.map(msg => {
                          const isPrescreenReq = msg.message_type === 'prescreen_request' || msg.message_type === 'prescreen_new_times';
                          const isPrescreenAvail = msg.message_type === 'prescreen_availability';
                          const isPrescreenConfirmed = msg.message_type === 'prescreen_confirmed';
                          if (isPrescreenReq && msg.sender_type === 'client') {
                            const slots = msg.metadata?.proposed_slots || [];
                            const isNewTimes = msg.message_type === 'prescreen_new_times';
                            const hasSlots = slots.length > 0;
                            return (
                              <div key={msg.id} className="flex justify-start">
                                <div className={`w-full max-w-sm rounded-2xl border-2 border-[#40E0D0]/40 overflow-hidden ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
                                  <div className="bg-gradient-to-r from-[#0066FF]/10 to-[#40E0D0]/20 px-4 py-3 border-b border-[#40E0D0]/20">
                                    <div className="flex items-center gap-2 mb-0.5">
                                      <Calendar className="w-4 h-4 text-[#40E0D0]" />
                                      <span className="text-sm font-bold">{isNewTimes ? 'New Interview Times Proposed' : 'Schedule Pre-Screen Interview'}</span>
                                    </div>
                                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{msg.metadata?.company_name} · {msg.metadata?.job_title}</p>
                                  </div>
                                  <div className="px-4 py-3 space-y-3">
                                    {hasSlots && (
                                      <div>
                                        <p className={`text-xs font-semibold mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Available times from hiring company:</p>
                                        <div className="space-y-1.5">
                                          {slots.map((s, i) => (
                                            <div key={i} className={`flex items-center gap-2 text-xs px-3 py-2 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                                              <Calendar className="w-3 h-3 text-[#40E0D0] flex-shrink-0" />
                                              <span className="font-medium">{new Date(s.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                                              <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>at {s.time}</span>
                                            </div>
                                          ))}
                                          {msg.metadata?.note && <p className={`text-xs mt-2 italic ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>"{msg.metadata.note}"</p>}
                                        </div>
                                      </div>
                                    )}
                                    {!hasSlots && <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Please share up to 3 times when you're available for a brief pre-screen call.</p>}
                                    <div className="flex gap-2">
                                      {hasSlots && (
                                        <button onClick={() => { setScheduleModal(msg); setScheduleMode('accept'); setAcceptedSlot(null); }}
                                          className="flex-1 py-2 rounded-xl bg-[#40E0D0] text-black text-xs font-semibold hover:bg-[#40E0D0]/90 transition-colors flex items-center justify-center gap-1.5">
                                          <CheckCircle className="w-3.5 h-3.5" />Accept a Time
                                        </button>
                                      )}
                                      <button onClick={() => { setScheduleModal(msg); setScheduleMode('propose'); setAvailSlots([{ date: '', time: '' }, { date: '', time: '' }, { date: '', time: '' }]); }}
                                        className={`flex-1 py-2 rounded-xl text-xs font-semibold border transition-colors flex items-center justify-center gap-1.5 ${isDark ? 'border-[#40E0D0] text-[#40E0D0] hover:bg-[#40E0D0]/10' : 'border-[#40E0D0] text-[#0066FF] hover:bg-[#40E0D0]/10'}`}>
                                        <Calendar className="w-3.5 h-3.5" />{hasSlots ? 'Other Times' : 'Submit Availability'}
                                      </button>
                                    </div>
                                  </div>
                                  <div className={`px-4 pb-2 text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{activeConv?.clientName} · {timeAgo(msg.created_at)}</div>
                                </div>
                              </div>
                            );
                          }
                          if (isPrescreenAvail && msg.sender_type === 'consultant') {
                            const slots = msg.metadata?.slots || [];
                            return (
                              <div key={msg.id} className="flex justify-end">
                                <div className={`max-w-sm rounded-2xl border border-[#40E0D0]/30 overflow-hidden ${isDark ? 'bg-gray-700' : 'bg-primary/10'}`}>
                                  <div className="px-4 pt-3 pb-1">
                                    <div className="flex items-center gap-2 mb-2">
                                      <CheckCircle className="w-4 h-4 text-[#40E0D0]" />
                                      <span className="text-sm font-semibold">Availability Submitted</span>
                                    </div>
                                    <div className="space-y-1 mb-2">
                                      {slots.map((s, i) => (
                                        <div key={i} className={`text-xs px-2.5 py-1.5 rounded-lg ${isDark ? 'bg-gray-600' : 'bg-white/60'}`}>
                                          {new Date(s.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} at {s.time}
                                        </div>
                                      ))}
                                    </div>
                                    <p className={`text-xs pb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>You · {timeAgo(msg.created_at)}</p>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          if (isPrescreenConfirmed) {
                            const slot = msg.metadata?.confirmed_slot;
                            return (
                              <div key={msg.id} className="flex justify-start">
                                <div className={`max-w-sm rounded-2xl border-2 border-emerald-400/40 overflow-hidden ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
                                  <div className="bg-emerald-50 px-4 py-3">
                                    <div className="flex items-center gap-2">
                                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                                      <span className="text-sm font-bold text-emerald-800">Interview Confirmed!</span>
                                    </div>
                                    {slot && <p className="text-sm font-semibold text-emerald-700 mt-1">{new Date(slot.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} at {slot.time}</p>}
                                    <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{activeConv?.clientName} · {timeAgo(msg.created_at)}</p>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          return (
                            <div key={msg.id} className={`flex ${msg.sender_type === 'consultant' ? 'justify-end' : 'justify-start'}`}>
                              <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${msg.sender_type === 'consultant' ? 'bg-primary text-black' : isDark ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900'}`}>
                                <p className="text-xs font-semibold mb-1 opacity-70">{msg.sender_type === 'consultant' ? 'You' : activeConv?.clientName} · {timeAgo(msg.created_at)}</p>
                                <p className="text-sm whitespace-pre-wrap">{msg.message_body}</p>
                              </div>
                            </div>
                          );
                        })}
                        <div ref={threadEndRef} />
                      </div>

                      <div className={`p-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                        {activeConv?.canReply ? (
                          <div className="flex gap-2 items-end">
                            <textarea value={msgDraft} onChange={e => setMsgDraft(e.target.value)} placeholder={`Reply to ${activeConv?.clientName}...`} rows={2}
                              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                              className={`flex-1 text-sm rounded-xl border px-3 py-2 resize-none focus:ring-2 focus:ring-primary focus:border-transparent outline-none ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`} />
                            <button onClick={sendMessage} disabled={msgSending || !msgDraft.trim()} className="w-9 h-9 bg-primary text-black rounded-xl flex items-center justify-center hover:bg-primary/90 disabled:opacity-40 flex-shrink-0 transition-colors">
                              <Send className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                            You can reply once this client sends you a message first.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

            </div>
          )}

          {/* Pre-screen scheduling modal */}
          {scheduleModal && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4" onClick={() => { setScheduleModal(null); setScheduleMode('accept'); }}>
              <div className={`max-w-md w-full rounded-2xl shadow-2xl overflow-hidden ${isDark ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`} onClick={e => e.stopPropagation()}>
                <div className="bg-gradient-to-r from-[#0066FF] to-[#40E0D0] p-5 text-white">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center"><Calendar className="w-5 h-5 text-white" /></div>
                      <div>
                        <h2 className="text-base font-bold">{scheduleMode === 'accept' ? 'Accept a Time Slot' : 'Propose Your Availability'}</h2>
                        <p className="text-white/80 text-xs mt-0.5">{scheduleModal.metadata?.company_name} · {scheduleModal.metadata?.job_title}</p>
                      </div>
                    </div>
                    <button onClick={() => setScheduleModal(null)} className="p-2 hover:bg-white/20 rounded-lg transition-colors"><X className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="p-5">
                  {scheduleMode === 'accept' && (() => {
                    const slots = scheduleModal.metadata?.proposed_slots || [];
                    return (
                      <div className="space-y-4">
                        <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Select the time that works best for you. The hiring company will receive your confirmation immediately.</p>
                        <div className="space-y-2">
                          {slots.map((s, i) => {
                            const isSelected = acceptedSlot?.date === s.date && acceptedSlot?.time === s.time;
                            return (
                              <button key={i} onClick={() => setAcceptedSlot(s)}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-left transition-all ${isSelected ? 'border-[#40E0D0] bg-[#40E0D0]/10' : isDark ? 'border-gray-700 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300'}`}>
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'border-[#40E0D0] bg-[#40E0D0]' : isDark ? 'border-gray-500' : 'border-gray-300'}`}>
                                  {isSelected && <div className="w-2 h-2 rounded-full bg-black" />}
                                </div>
                                <div>
                                  <p className="text-sm font-semibold">{new Date(s.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
                                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{s.time}</p>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                        <div className="flex gap-2 pt-1">
                          <button onClick={() => setScheduleMode('propose')} className={`flex-1 py-2.5 rounded-xl text-sm font-medium border transition-colors ${isDark ? 'border-gray-700 text-gray-400 hover:bg-gray-800' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}>None work — propose other times</button>
                          <button onClick={submitAvailability} disabled={submittingAvail || !acceptedSlot}
                            className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-[#40E0D0] text-black hover:bg-[#40E0D0]/90 transition-colors disabled:opacity-50">
                            {submittingAvail ? 'Confirming...' : 'Confirm This Time'}
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                  {scheduleMode === 'propose' && (
                    <div className="space-y-4">
                      <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Provide up to 3 times when you're available. The hiring company will confirm one.</p>
                      {availSlots.map((slot, i) => (
                        <div key={i} className="flex gap-2 items-center">
                          <span className={`text-xs font-semibold w-16 flex-shrink-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Option {i + 1}</span>
                          <input type="date" value={slot.date} onChange={e => setAvailSlots(prev => prev.map((s, j) => j === i ? { ...s, date: e.target.value } : s))}
                            className={`flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent outline-none ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'}`} />
                          <input type="time" value={slot.time} onChange={e => setAvailSlots(prev => prev.map((s, j) => j === i ? { ...s, time: e.target.value } : s))}
                            className={`w-28 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent outline-none ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300'}`} />
                        </div>
                      ))}
                      <div className="flex gap-2 pt-1">
                        {(scheduleModal.metadata?.proposed_slots || []).length > 0 && (
                          <button onClick={() => setScheduleMode('accept')} className={`py-2.5 px-4 rounded-xl text-sm font-medium border transition-colors ${isDark ? 'border-gray-700 text-gray-400 hover:bg-gray-800' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}>Back</button>
                        )}
                        <button onClick={() => { setScheduleModal(null); setScheduleMode('accept'); }} className={`flex-1 py-2.5 rounded-xl text-sm font-medium border transition-colors ${isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>Cancel</button>
                        <button onClick={submitAvailability} disabled={submittingAvail || availSlots.every(s => !s.date || !s.time)}
                          className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-[#40E0D0] text-black hover:bg-[#40E0D0]/90 transition-colors disabled:opacity-50">
                          {submittingAvail ? 'Sending...' : 'Send My Availability'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

        </div>
      </main>
    </div>
  );
}
